"use client";

import { notifications } from "@mantine/notifications";
import AccountsTab from "Components/Admin/AdminSettings/Accounts/AccountsTab";
import ConfigurationTab from "Components/Admin/AdminSettings/Configuration/ConfigurationTab";
import IntegrationTab from "Components/Admin/AdminSettings/Integration/IntegrationTab";
import SecurityTab from "Components/Admin/AdminSettings/Security/SecurityTab";
import React, { useEffect, useRef, useState } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { useGetConfigurationByPlantQuery, useUpdateConfigurationMutation } from "Services/Admin/ConfigurationApiSlice";
import { resetChanges, setConfig } from "State/Admin/AdminSettings/ConfigurationSlice";
import { useAppDispatch, useAppSelector } from "hooks";

type TabType = "Social Media" | "security" | "configuration" | "integration";

const AdminSettings: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const pathToTab: Record<string, TabType> = {
    "social-media": "Social Media",
    security: "security",
    configuration: "configuration",
    integration: "integration",
  };

  const tabToPath: Record<TabType, string> = {
    "Social Media": "social-media",
    security: "security",
    configuration: "configuration",
    integration: "integration",
  };

  const currentPath = location.pathname.split("/").filter(Boolean).pop() || "";
  const activeTab = pathToTab[currentPath] || "Social Media";

  const PLANT = process.env.REACT_APP_PLANT || "";
  const adminToken = useAppSelector((state) => state.adminlogin.adminToken);
  const { data: existingConfig, isLoading, isSuccess } = useGetConfigurationByPlantQuery(PLANT, { skip: !adminToken });
  const [updateConfiguration] = useUpdateConfigurationMutation();
  const dispatch = useAppDispatch();
  const { config, hasChanges } = useAppSelector((state: any) => state.configuration);
  const saveLockRef = useRef(false);
const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isSuccess && existingConfig) {
      dispatch(setConfig(existingConfig));
    }
  }, [existingConfig, isSuccess, dispatch]);

 const handleTabChange = (tab: TabType) => {
   navigate(`/admin/settings/${tabToPath[tab]}`);
 };


  const handleCancel = () => {
    if (existingConfig) {
      dispatch(setConfig(existingConfig));
      dispatch(resetChanges());
    }
  };

  const handleSave = async () => {
  if (saveLockRef.current || !hasChanges) return; 

  saveLockRef.current = true;
  setIsSaving(true);

  try {
    const res = await updateConfiguration(config).unwrap();

    notifications.show({
      id: "settings-save-success",
      message: res.message,
      color: "green",
    });

    dispatch(resetChanges());

  } catch (error: any) {
    notifications.show({
      id: "settings-save-error",
      message: error?.data?.message || "An error occurred",
      color: "red",
    });
  } finally {
    saveLockRef.current = false;
    setIsSaving(false);
  }
};

  return (
    <div className="font-body p-6 mx-auto">
      <header className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight">Settings</h1>
        <div className="flex items-center gap-3">
          <button
            onClick={handleCancel}
            disabled={!hasChanges}
            className={`px-4 py-2 text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50 ${hasChanges ? "text-blue-600" : "text-gray-400 cursor-not-allowed"
              }`}
          >
            Cancel
          </button>
          <button
  onClick={handleSave}
  disabled={!hasChanges || isSaving}
  className={`px-4 py-2 text-sm text-white rounded-md flex items-center gap-2
    ${
      hasChanges && !isSaving
        ? "bg-blue-600 hover:bg-blue-700"
        : "bg-gray-400 cursor-not-allowed"
    }`}
>
  {isSaving && (
    <svg
      className="w-4 h-4 animate-spin"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8v8H4z"
      />
    </svg>
  )}
  {isSaving ? "Saving..." : "Save"}
</button>
        </div>
      </header>

<div className="mt-6 bg-white rounded-lg border shadow-sm flex flex-col xl:flex-row w-full overflow-visible">



 <div
  className="
    w-full xl:w-56
    border-b xl:border-b-0 xl:border-r
    bg-gray-50
    py-2 xl:py-4
    flex xl:block
    overflow-x-auto xl:overflow-visible
  "
>


    {(["Social Media", "security", "configuration", "integration"] as TabType[]).map((tab) => {
      const isActive = activeTab === tab;

      return (
        <button
          key={tab}
          onClick={() => handleTabChange(tab)}
         className={`
  w-auto xl:w-full
  shrink-0
  text-left px-4 py-3 text-sm font-medium whitespace-nowrap
  ${
    isActive
      ? "bg-white text-blue-600 xl:border-l-4 xl:border-blue-600 border-b-2 xl:border-b-0 border-blue-600"
      : "text-gray-600 hover:bg-gray-100"
  }
`}

        >
          {tab.charAt(0).toUpperCase() + tab.slice(1)}
        </button>
      );
    })}
  </div>

  {/* RIGHT CONTENT */}
  <div className="flex-1 p-4 xl:p-6 w-full min-w-0">


    <Routes>
      <Route index element={<Navigate to="social-media" replace />} />
      <Route path="social-media" element={<AccountsTab />} />
      <Route path="security" element={<SecurityTab />} />
      <Route path="configuration" element={<ConfigurationTab />} />
      <Route path="integration" element={<IntegrationTab />} />
    </Routes>
  </div>

</div>

    </div>
  );
};

export default AdminSettings;
